<script setup lang="ts">
import { Head, Link } from '@inertiajs/vue3'
import InternalLayout from '@/layouts/InternalLayout.vue'

interface Author {
  id: number
  name: string
}

interface Post {
  id: number
  title: string
  slug: string
  excerpt: string | null
  featured_image: string | null
  featured_image_alt: string | null
  published_at: string
  author: Author
}

interface PaginatedPosts {
  data: Post[]
  links: Array<{ url: string | null; label: string; active: boolean }>
  current_page: number
  last_page: number
}

defineProps<{
  posts: PaginatedPosts
}>()

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}
</script>

<template>
  <InternalLayout title="Blog" hero-image="/img/fresh-cut-grass-mow-stripes.jpg">
    <Head title="Blog">
      <meta head-key="description" name="description" content="Read the latest lawn care tips, news, and insights from the Mow Managers team." />
      <link rel="canonical" href="https://mowmanagers.com/blog" />
      <meta property="og:title" content="Blog - Mow Managers" />
      <meta property="og:description" content="Read the latest lawn care tips, news, and insights from the Mow Managers team." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://mowmanagers.com/blog" />
    </Head>

    <section aria-label="Blog posts" class="py-16 lg:py-24 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div v-if="posts.data.length === 0" class="text-center py-16">
          <p class="text-[#6d706d] text-[17px]">No posts yet. Check back soon!</p>
        </div>

        <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <Link
            v-for="post in posts.data"
            :key="post.id"
            :href="`/${post.slug}`"
            class="group block bg-white rounded-[20px] overflow-hidden shadow-sm hover:shadow-md transition-shadow"
          >
            <div class="aspect-[16/9] overflow-hidden bg-gray-100">
              <img
                v-if="post.featured_image"
                :src="`/storage/${post.featured_image}`"
                :alt="post.featured_image_alt || post.title"
                class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div v-else class="w-full h-full flex items-center justify-center bg-mm-light">
                <span class="text-mm-green text-4xl font-bold">MM</span>
              </div>
            </div>

            <div class="p-6">
              <div class="flex items-center gap-2 text-[13px] text-[#6d706d]">
                <span>{{ post.author.name }}</span>
                <span>&middot;</span>
                <time :datetime="post.published_at">{{ formatDate(post.published_at) }}</time>
              </div>

              <h2 class="text-mm-dark text-xl font-bold mt-2 group-hover:text-mm-green transition-colors">
                {{ post.title }}
              </h2>

              <p v-if="post.excerpt" class="text-[#6d706d] text-[15px] leading-[24px] mt-3 line-clamp-3">
                {{ post.excerpt }}
              </p>
            </div>
          </Link>
        </div>

        <nav v-if="posts.last_page > 1" aria-label="Pagination" class="mt-12 flex justify-center gap-2">
          <template v-for="link in posts.links" :key="link.label">
            <Link
              v-if="link.url"
              :href="link.url"
              class="px-4 py-2 rounded-md text-sm font-medium transition-colors"
              :class="link.active ? 'bg-mm-green text-white' : 'bg-gray-100 text-mm-dark hover:bg-gray-200'"
              v-html="link.label"
            />
            <span
              v-else
              class="px-4 py-2 rounded-md text-sm font-medium text-gray-400 bg-gray-50"
              v-html="link.label"
            />
          </template>
        </nav>
      </div>
    </section>
  </InternalLayout>
</template>
